/* Мост «Своего референса» для движков шаблона (engine-kit, план M3 §2 S6 L-R; photo P3, video, shoot, pshoot): BotRef.make({...bridge.opts(), id, …})
   поверх ручной правки legacy/bot-ref.js (o.upload / o.remove). BotRef остаётся источником правды о выборе (файлы, «Ваша», unpick), мост — о сервере:
   - opts().upload(file, e) — проверку картинки ведёт сервер, а не [ДЕМО]-таймер: загрузка идёт через очередь media-bridge (media.upload — одна FIFO
     на кадр, лист согласия, замок и политика поля формы; сервис грузит по одному файлу), POST /app/uploads {purpose: 'reference', kind: 'photo',
     use — из поля формы}. Принят — {upload_id}; отказ с кодом проверки (not_allowed, too_small, unreadable) — {reason}: подсказка BotRef
     «Картинка не подошла: …»; иной отказ (политика поля, замок, текст сервиса) — {text}; лист согласия закрыли, запись убрали, кадр сменился — {};
     транспорт не подтвердил — запись ждёт «Продолжить загрузку» (media.resume), BotRef держит «Проверяем»;
   - opts().remove(e) — запись ушла из референса («Убрать», «Повторить», замена одной картинки): её ещё не начатая загрузка снимается;
   - inputsFor(prev, REF) — входы заказа: ключи референса формы (reference или reference_1…reference_9) по REF.order(), то есть только выбранный
     и проверенный референс, по порядку и подряд; невыбранный (unpick) или убранный — ключи сняты; JSON-поле — id в JSON;
   - restore(inputs) — «Повторить», «Изменить»: превью загрузок из входов (uploadPreview: владелец до и после, тип картинки, вес; 30 с на всё)
     → файлы снимка для REF.restore({files}) с прежними upload_id; не открылось (решение владельца Q7) — файл не возвращается, причина словами.
   Адреса blob превью создаёт мост и освобождает в cancel(). */
import { endpoints } from '../../../api';
import { uiText } from '../../../i18n';
import { studioFields, type StudioForm } from '../studio-api';
import { mediaFile, type MediaBridge, type MediaMeta } from './media-bridge';
import { uploadPurpose, type OrderService } from './order-service';

type Inputs = Record<string, string>;
/** Запись файла BotRef (bot-ref.js make: S.files) — то, что o.upload / o.remove получают вторым аргументом */
export interface RefEntry {
  key: number;
  name?: string;
  size?: number;
  url?: string;
  w?: number;
  h?: number;
  upload_id: string | null;
  st: string;
}
/** Коды отказа проверки референса, у которых у BotRef есть свои подсказки (WHY) */
export const REF_REASONS = ['not_allowed', 'too_small', 'unreadable'] as const;
export type RefReason = (typeof REF_REASONS)[number];
/** {} — отменили (лист согласия закрыли, запись убрали, кадр сменился): BotRef убирает запись молча */
export type RefNone = { upload_id?: undefined; reason?: undefined; text?: undefined };
/** Ответ o.upload для BotRef (L-R) */
export type RefAnswer ={ upload_id: string } | { reason: RefReason } | { text: string } | RefNone;
/** Опции BotRef.make от сервера (L-R) */
export interface RefOpts {
  upload(file: File, e: RefEntry): Promise<RefAnswer>;
  remove(e: RefEntry): void;
}
/** Что мост читает у REF (BotRef.make) */
export interface RefLike {
  active(): boolean;
  order(): Array<{ upload_id: string | null; use?: string }>;
}
/** Файл снимка BotRef (snap().files) для REF.restore({files}) */
export interface RefFile {
  upload_id: string;
  name: string;
  size: number;
  w: number;
  h: number;
  url: string;
}
/** Итог восстановления: файлы, которые открылись (по порядку входов), и причина словами, если какие-то нет */
export interface RefRestore {
  files: RefFile[];
  text: string;
}
export interface RefBridgeOptions {
  /** Мост загрузок кадра: его очередь — общая для всех загрузок кадра */
  media: MediaBridge;
  svc: OrderService;
  owner(): number | undefined;
  /** Ключи референса по порядку; по умолчанию — referenceKeys(форма сервиса) */
  keys?(): string[];
}
export interface RefBridge {
  /** Ключи референса формы по порядку (пусто — форма референс не публикует: экран его не показывает) */
  keys(): string[];
  has(): boolean;
  opts(): RefOpts;
  /** Входы с ключами референса по REF.order() (остальные ключи референса сняты) */
  inputsFor(prev: Inputs, ref: RefLike): Inputs;
  /** id загрузок референса во входах по порядку ключей */
  ids(inputs?: Inputs): string[];
  /** Превью загрузок из входов (по умолчанию — входы сервиса); null — устарело (новое восстановление, cancel) */
  restore(inputs?: Inputs): Promise<RefRestore | null>;
  /** Снять ждущие загрузки и чтения, освободить адреса превью */
  cancel(): void;
}

const MB = 1024 * 1024;
/** Типы превью, которые BotRef показывает (как media-bridge для фото) */
const PREVIEW = ['image/jpeg', 'image/png', 'image/webp'];
const num = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0;
const isReason = (value: unknown): value is RefReason => REF_REASONS.includes(value as RefReason);

/** Поля загрузки референса формы (purpose 'reference' или purposes ['reference']): reference, затем reference_1…reference_N по номеру */
export function referenceKeys(form: StudioForm | null | undefined): string[] {
  const order = (key: string) => (key === 'reference' ? 0 : Number(/_(\d+)$/.exec(key)?.[1] ?? 1000));
  return studioFields(form)
    .filter(field => field.upload && field.upload.kind === 'photo' && uploadPurpose(field.upload) === 'reference')
    .map(field => field.key)
    .sort((a, b) => order(a) - order(b));
}

export function createRefBridge(o: RefBridgeOptions): RefBridge {
  const { svc, media } = o;
  /** Загрузки записей BotRef в очереди или в полёте: номер записи → прерывание */
  const flights = new Map<number, AbortController>();
  const owned = new Set<string>();
  let reader: AbortController | null = null;
  let seq = 0;

  const keys = () => (o.keys ? o.keys() : referenceKeys(svc.get().form));
  const json = (key: string) => studioFields(svc.get().form).find(item => item.key === key)?.value_encoding === 'json';
  const decode = (key: string, value: string | undefined) => {
    if (!value) return '';
    if (!json(key)) return value;
    try {
      const id = JSON.parse(value) as unknown;
      return typeof id === 'string' ? id : '';
    } catch {
      return '';
    }
  };
  const ids = (inputs: Inputs = svc.get().inputs) => keys().map(key => decode(key, inputs[key])).filter(Boolean);

  const opts: RefOpts = {
    async upload(file, e) {
      const key = keys()[0];
      if (!key) return {};
      flights.get(e.key)?.abort();
      const flight = new AbortController();
      flights.set(e.key, flight);
      try {
        const meta: MediaMeta | undefined = num(e.w) && num(e.h) ? { width: e.w, height: e.h } : undefined;
        const answer = await media.upload(key, file, meta, flight.signal);
        if ('id' in answer) return { upload_id: answer.id };
        if ('cancelled' in answer) return {};
        return isReason(answer.reason) ? { reason: answer.reason } : { text: answer.text };
      } finally {
        if (flights.get(e.key) === flight) flights.delete(e.key);
      }
    },
    remove(e) {
      const flight = flights.get(e.key);
      if (!flight) return;
      flights.delete(e.key);
      flight.abort();
    },
  };

  async function restore(inputs?: Inputs): Promise<RefRestore | null> {
    reader?.abort();
    const list = ids(inputs);
    const owner = o.owner();
    if (!list.length) {
      reader = null;
      return { files: [], text: '' };
    }
    const mine = ++seq;
    const request = new AbortController();
    reader = request;
    let timedOut = false;
    const deadline = window.setTimeout(() => {
      timedOut = true;
      request.abort();
    }, 30000);
    let read: Array<{ id: string; blob: Blob; w: number; h: number } | null>;
    try {
      if (!owner) throw new Error('owner_unknown');
      const me = await endpoints.me();
      if (me.user.id !== owner) throw new Error('owner_changed');
      read = await Promise.all(
        list.map(async id => {
          try {
            const blob = await endpoints.uploadPreview(id, request.signal);
            if (request.signal.aborted) throw new Error('aborted');
            if (!PREVIEW.includes(blob.type) || !blob.size || blob.size > 20 * MB) throw new Error('preview_invalid');
            const size = await mediaFile.readImageSize(new File([blob], 'preview', { type: blob.type }));
            return { id, blob, w: size?.width ?? 0, h: size?.height ?? 0 };
          } catch {
            return null;
          }
        }),
      );
      const again = await endpoints.me();
      if (again.user.id !== owner) throw new Error('owner_changed');
    } catch {
      read = list.map(() => null);
    }
    clearTimeout(deadline);
    if (mine !== seq || (request.signal.aborted && !timedOut)) return null;
    reader = null;
    const files: RefFile[] = [];
    for (const item of read) {
      if (!item) continue;
      const url = URL.createObjectURL(item.blob);
      owned.add(url);
      files.push({ upload_id: item.id, name: uiText('Фото'), size: item.blob.size, w: item.w, h: item.h, url });
    }
    const lost = files.length < list.length;
    return {
      files,
      text: lost
        ? uiText(list.length > 1 ? 'Некоторые файлы повтора больше недоступны. Загрузите их заново.' : 'Не удалось открыть фото. Загрузите его заново.')
        : '',
    };
  }

  return {
    keys,
    has: () => keys().length > 0,
    opts: () => opts,
    inputsFor(prev, ref) {
      const list = keys();
      const next = { ...prev };
      list.forEach(key => delete next[key]);
      if (!ref.active()) return next;
      ref
        .order()
        .map(item => item.upload_id)
        .filter((id): id is string => !!id)
        .slice(0, list.length)
        .forEach((id, i) => {
          next[list[i]] = json(list[i]) ? JSON.stringify(id) : id;
        });
      return next;
    },
    ids,
    restore,
    cancel() {
      seq++;
      reader?.abort();
      reader = null;
      [...flights.values()].forEach(flight => flight.abort());
      flights.clear();
      for (const url of owned) URL.revokeObjectURL(url);
      owned.clear();
    },
  };
}
